/**
 * "Group these for me" — the classification editor's AI.
 *
 * The classification studio had NO AI at all: a user with 400 unsorted photos
 * had to drag every one into a class by hand, while the detection studio next
 * door had a whole auto-label panel. This is the missing half.
 *
 * It drives `POST /cluster/auto` (ResNet18 embeddings -> KMeans), which already
 * existed and was reachable only from the assistant.
 *
 * Everything here is pure over plain data — no React, no network, no File — so
 * the index arithmetic that makes or breaks it is testable. That arithmetic is
 * the whole risk: **the endpoint's `index` is an index into the REQUEST**, and
 * the request is a capped, evenly-spread SAMPLE of the unlabeled pool. Mapping
 * it back against the full pool instead of the sample hands each image its
 * neighbour's group, silently.
 */

/** One request's worth. Matches the server's own cap. */
export const MAX_GROUPING_SAMPLE = 240;

/** KMeans needs a reason to exist. */
export const MIN_GROUPING_IMAGES = 4;

/**
 * An evenly spaced sample, so every part of the pool is represented.
 *
 * Taking the first N would group only the images that happen to sort first —
 * which, for a folder of `cam1_*` then `cam2_*`, means one camera.
 */
export const sampleForGrouping = (images = [], limit = MAX_GROUPING_SAMPLE) => {
  const list = images || [];
  if (list.length <= limit) return [...list];
  const stride = list.length / limit;
  const picked = [];
  for (let i = 0; i < limit; i += 1) picked.push(list[Math.floor(i * stride)]);
  return picked;
};

/**
 * Turn the endpoint's answer into per-image class assignments.
 *
 * `sample` MUST be the exact array that was uploaded, in order — see the note
 * at the top of this file.
 */
export const planAssignments = ({ assignments = [], sample = [], classes = [] }) => {
  const perImage = [];
  const counts = new Map();

  assignments.forEach((entry) => {
    const image = sample[entry.index];
    if (!image) return; // an index we never sent; ignore rather than guess
    // `Number(null)` is 0 and `Number.isInteger(0)` is true, so a null cluster
    // id would quietly become cluster 0 — every unassignable image dumped into
    // the first class. Reject the absent case before coercing.
    const raw = entry.cluster_id;
    if (raw === null || raw === undefined || raw === '') return;
    const clusterId = Number(raw);
    if (!Number.isInteger(clusterId) || clusterId < 0) return;
    perImage.push({ imageId: image.id, clusterId, name: image.name });
    counts.set(clusterId, (counts.get(clusterId) || 0) + 1);
  });

  const clusterIds = [...counts.keys()].sort((a, b) => a - b);

  // Cluster 0..k-1 maps onto the classes that already exist, in order; any
  // shortfall is reported so the caller can create them BEFORE assigning.
  const mapping = new Map();
  clusterIds.forEach((clusterId, position) => {
    mapping.set(clusterId, classes[position] ? classes[position].id : null);
  });

  return {
    perImage: perImage.map((entry) => ({ ...entry, classId: mapping.get(entry.clusterId) })),
    groups: clusterIds.map((clusterId, position) => ({
      clusterId,
      count: counts.get(clusterId),
      classId: mapping.get(clusterId),
      position,
    })),
    classesNeeded: Math.max(0, clusterIds.length - classes.length),
    grouped: perImage.length,
  };
};

/**
 * Which images to send, and which to leave alone.
 *
 * Anything beyond the sample stays unlabeled rather than being guessed at — the
 * endpoint returns assignments only for what it was given, and inventing a
 * group for the rest would be fabrication.
 */
export const splitForGrouping = (unstructured = [], limit = MAX_GROUPING_SAMPLE) => {
  const sample = sampleForGrouping(unstructured, limit);
  const sampled = new Set(sample.map((image) => image.id));
  return {
    sample,
    untouched: unstructured.filter((image) => !sampled.has(image.id)),
  };
};

/** Is there enough here for grouping to mean anything? */
export const canGroup = (unstructured = []) => (unstructured || []).length >= MIN_GROUPING_IMAGES;
